<template>
  <div id="bank-slip-success-page">
    <div class="success-page__header">
      <img :src="iconSuccessURL" alt="Sucesso!">
      <h2>Seu pagamento foi agendado com sucesso</h2>

      <p>
        <strong>
          O débito em conta corrente pode levar até 6 dias úteis para ser processado.
        </strong>

        <br />

        Após a confirmação do pagamento em nosso sistema, enviaremos
        um e-mail para <strong>{{ emailString }}</strong> com os
        detalhes da sua {{ device.payment.isRenovation ? 'renovação' : 'compra' }} e o bilhete do seu seguro.
      </p>

      <p>
        <strong>Atenção:</strong> sempre verifique a sua caixa de spam,
        pois o e-mail pode ser direcionado para lá.
      </p>
    </div>

    <div class="success-page__content">

      <NPS />

      <div class="success-page__block">
        <h3>Resumo da {{ device.payment.isRenovation ? 'renovação' : 'compra' }}</h3>

        <div class="success-page__block__row">
          <p>
            <span>Subtotal:</span>
            <strong>R$ {{ device.payment.amount }}</strong>
          </p>
        </div>

        <div class="success-page__block__row row-footer">
          <p>
            <span>Valor Total:</span>
            <strong>R$ {{ device.payment.amount }}</strong>
          </p>
        </div>
      </div>

      <div class="success-page__block inline-box">
        <div class="success-page__block__item">
          <img :src="iconCalendarURL" alt="Calendário">
          <h5>Débito Agendado</h5>
        </div>

        <div class="success-page__block__item">
          <span>Agência: </span> {{ agencyString }}
        </div>

        <div class="success-page__block__item">
          <span>Conta: </span> {{ accountString }}
        </div>

        <div class="success-page__block__item">
          {{ installmentsString }}
        </div>
      </div>

      <div class="success-page__block">
        <div class="success-page__accordion">
          <div class="success-page__accordion-head">
            <div class="success-page__accordion-head-title">
              <img :src="iconDeviceURL" alt="Aparelho">
              <span>{{ device.nome | capitalize }}</span>
            </div>

            <button v-if="isDesktop" @click.prevent.stop="toggleDetails" :class="{'opened': showDetails}">
              <span>Ver detalhes</span>
            </button>
          </div>

          <div v-if="showDetails" class="success-page__accordion-content">
            <div class="success-page__accordion-content-row">
              <img :src="iconCoverageURL" alt="Cobertura">
              <p>
                <strong>Cobertura: </strong>
                <span>Roubo</span>
                <span v-if="somenteRoubo"> e Furto</span>
                <span v-else>, Furto, Quebra, Líquido</span>
              </p>
            </div>

            <div class="success-page__accordion-content-row">
              <img :src="iconValidityURL" alt="Vigência">
              <p>
                <strong>Vigência do seguro: </strong>
                <span>12 meses (Franquia de 25% do valor do aparelho parcelada em até 4x)</span>
              </p>
            </div>

            <div v-if="device.payment.isRenovation" class="success-page__accordion-content-row">
              <img :src="iconCoverageURL" alt="Ícone documento">
              <p>
                <strong>{{ device.payment.isRenovation ? 'Seguro sem carência: ': 'Carência de 30 dias: '}}</strong>
                <span>Vigência iniciada em {{ device.renewalStartDate }}</span>
              </p>
            </div>

            <div v-if="hasProtectedBag" class="success-page__accordion-content-row">
              <img :src="iconCoverageURL" alt="Cobertura">
              <p>
                <strong>{{ device.protectedBag.bagName }} </strong>
                <span>(Transações digitais, pix e itens da bolsa)</span>
              </p>
              <a class="terms" :href="device.protectedBag.termsLink" target="_blank">
                <img class="terms__icon" :src="iconDocument" alt="Ícone documento">
                <span class="terms__title">Condições gerais</span>
              </a>
            </div>
          </div>

          <div class="success-page__accordion-footer">
            <Button
              label="Ver condições gerais do seu seguro"
              icon="document-purple"
              @click.prevent.stop="clickToOpen(generalConditionsURL)"
            />
          </div>
        </div>
      </div>

      <div class="success-page__block alert-box">
        <h3>Orientações</h3>

        <ul>
          <li>
            Pedir autorização para liberação do débito agendado
            no App BB, Internet Banking e caixa eletrônico;
          </li>

          <li>
            Solicitar para que o cliente verifique de imediato,
            se possível, a chegada do e-mail de confirmação de
            agendamento;
          </li>

          <li>
            Sinalizá-lo de que ele terá 30 dias para enviar
            o número de IMEI no link enviado por e-mail, para
            que o seguro seja ativado.
          </li>
        </ul>
      </div>

      <div class="success-page__footer">
        <Button
          label="Continuar navegando"
          icon="chevron-left"
          theme="link"
          @click="$router.push('/')"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Button from '@/components/ui/Button'
import { mapState } from 'vuex'

import NPS from '@/components/NPS/index.vue'

export default {
  name: 'ScheduledDebit',
  components: {
    Button,
    NPS
  },
  data () {
    return {
      showDetails: false,
      isDesktop: true
    }
  },
  computed: {
    ...mapState(['device']),
    somenteRoubo () {
      return this.device.somenteRoubo
    },
    iconSuccessURL () {
      return require(`@/assets/svg/icon-success-purple.svg`)
    },
    iconDeviceURL () {
      const name = this.device.deviceTypeName === 'celular' ? 'cellphone' : this.device.deviceTypeName
      return require(`@/assets/svg/icon-device-${name}.svg`)
    },
    iconCalendarURL () {
      return require(`@/assets/svg/icon-calendar-purple.svg`)
    },
    iconCoverageURL () {
      return require(`@/assets/svg/icon-coverage-purple.svg`)
    },
    iconValidityURL () {
      return require(`@/assets/svg/icon-validity-purple.svg`)
    },
    iconDocument () {
      return require(`@/assets/svg/icon-document-purple.svg`)
    },
    generalConditionsURL () {
      return `${process.env.VUE_APP_CHICO_XAVIER}/Ciclic_SeguroCelular_Condicoes.pdf`
    },
    emailString () {
      return this.device.personalData.email
        ? this.device.personalData.email
        : 'o e-mail de cadastro '
    },
    deviceIdString () {
      return this.device.deviceTypeName === 'celular'
        ? 'IMEI'
        : 'número de série'
    },
    agencyString () {
      return `••${this.device.payment.lastDigitsAgency}`
    },
    accountString () {
      return `••••••••••••${this.device.payment.lastDigitsAccount}`
    },
    installmentsString () {
      const installments = parseInt(this.device.payment.instalmment)
      return installments > 1 ? `${installments}x sem juros` : 'À vista'
    },
    hasProtectedBag () {
      return !!this.device.protectedBag
    }
  },
  mounted () {
    const windowSize = window.innerWidth

    if (windowSize <= 768) {
      this.isDesktop = false
      this.showDetails = true
    }
  },
  methods: {
    clickToOpen (url) {
      return window.open(url, '_blank')
    },
    clickToCopy (text, action) {
      const actions = {
        digitableLine: ' O código de barras foi copiado para sua área de transferência.'
      }

      this.showToast('success', actions[action])
      return navigator.clipboard.writeText(text)
    },
    toggleDetails () {
      this.showDetails = !this.showDetails
    },
    showToast (icon, msg) {
      const Toast = this.$swal.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.addEventListener('mouseenter', this.$swal.stopTimer)
          toast.addEventListener('mouseleave', this.$swal.resumeTimer)
        }
      })

      Toast.fire({
        icon,
        title: msg
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../SuccessPage.scss';

#bank-slip-success-page {
  align-items: center;
  display: flex;
  flex-direction: column;

  .success-page__header {
    max-width: 752px;
    text-align: center;
    padding-top:32px;
    width: 100%;

    img {
      margin-bottom: 12px;
    }
  }

  .success-page__content {
    max-width: 944px;
    width: 100%;

    .success-page__block {
      align-items: center;
      flex-shrink: 0;
      flex-wrap: wrap;
      justify-content: flex-end;

      &.alert-box {
        background-color: transparent;
        justify-content: flex-start;

        p {
          color: #707070;
          font-family: Rubik, sans-serif;
          font-style: normal;
          font-weight: normal;
          font-size: 16px;
          line-height: 24px;
          display: flex;
          align-items: center;
        }

        button {
          margin-left: 0;
          margin-top: 32px;
        }
      }

      &.inline-box {
        justify-content: space-between;
      }

      span {
        margin-right: auto;
      }

      button {
        margin-top: 16px;
        width: 100%;

        @media (min-width: 768px) {
          margin-left: 16px;
          margin-top: 0;
          width: fit-content;
        }
      }

      ul {
        list-style-type: none;
        margin-top: 16px;

        li {
          color: #4E5356;
          font-family: Rubik, sans-serif;
          font-style: normal;
          font-weight: normal;
          font-size: 16px;
          line-height: 24px;
          padding-left: 20px;
          position: relative;

          + li {
            margin-top: 24px;
          }

          &::before {
            content: "•";
            position: absolute;
            left: 0;
          }
        }
      }

      &__item {
        align-items: center;
        color: #707070;
        display: flex;
        font-family: Rubik, sans-serif;
        font-style: normal;
        font-weight: 500;
        font-size: 16px;
        line-height: 19px;

        img {
          height: 24px;
          margin-right: 20px;
          width: 24px;
        }

        h5 {
          color: #444444;
          font-family: Oscine, sans-serif;
          font-style: normal;
          font-weight: bold;
          font-size: 18px;
          line-height: 23px;
          margin: 0;
        }

        span {
          color: #444444;
          display: inline-block;
          margin-right: 5px;
        }
      }

      &__row {
        flex-shrink: 0;
        flex-wrap: wrap;

        p {
          display: flex;
          margin: 0;
          width: 100%;

          + p {
            margin-top: 12px;
          }

          span {
            color: #444444;
            font-family: Rubik, sans-serif;
            font-style: normal;
            font-weight: 400;
            font-size: 16px;
            line-height: 19px;
          }

          strong {
            color: #444444;
            font-family: Rubik, sans-serif;
            font-style: normal;
            font-weight: 700;
            font-size: 16px;
            line-height: 19px;
            margin-left: auto;
            text-align: right;
          }
        }
      }

      .success-page__accordion {
        width: 100%;

        &-head {
          align-items: center;
          display: flex;
          justify-content: space-between;
          margin-top: 10px;

          &-title {
            align-items: center;
            display: flex;
            justify-content: flex-start;

            img {
              margin-right: 16px;
              width: 32px;
            }

            span {
              color: #444444;
              font-family: Oscine, sans-serif;
              font-style: normal;
              font-weight: bold;
              font-size: 18px;
              line-height: 23px;
            }
          }

          button {
            &.opened {
              span {
                &::before {
                  transform: rotate(0);
                  transition: all ease 0.2s;
                }
              }
            }

            &:focus {
              outline:0;
            }

            span {
              display: inline-block;
              padding-right: 20px;
              position: relative;

              &::before {
                background-image: url(~@/assets/svg/chevron-up.svg);
                content: '';
                height: 7px;
                position: absolute;
                right: 0;
                top: calc(50% - 3.5px);
                transform: rotate(180deg);
                transition: all ease 0.2s;
                width: 14px;
              }
            }
          }
        }

        &-content {
          margin-top: 22px;

          &-row {
            align-items: center;
            border-top: solid 1px #BDC2C630;
            display: flex;
            padding: 20px 0;
            justify-content: flex-start;
            text-align: left;

            &:last-child {
              padding-bottom: 0;
            }

            img:not(.terms__icon) {
              margin-right: 16px;
              width: 32px;
            }

            p {
              margin: 0;

              span {
                color: #444444;
                font-family: Oscine, sans-serif;
                font-style: normal;
                font-weight: 400;
                font-size: 16px;
                line-height: 21px;
              }

              strong {
                color: #444444;
                font-family: Oscine, sans-serif;
                font-style: normal;
                font-weight: 700;
                font-size: 16px;
                line-height: 21px;
              }
            }
          }
        }

        &-footer {
          border-top: solid 1px #BDC2C630;
          margin: 24px 0 8px;
          padding-top: 32px;

          button {
            margin: 0;
            width: 100%;

            @media (min-width: 768px) {
              width: fit-content;
            }
          }
        }
      }
    }

    .success-page__footer {
      align-items: center;
      display: flex;
      justify-content: center;
      padding: 30px 0;
    }
  }

  .terms {
    margin-left: auto;
    display: flex;
    align-items: center;
    font-weight: bold;
    color: #3F4956;

    @media (max-width: 959px) {
      font-size: 11px;
      flex-direction: column;
      text-align: center;
    }

    @media (max-width: 767px) {
      max-width: 67px;
    }

    &__icon {
      max-width: 20px;
      margin-right: 7px;

      @media (max-width: 959px) {
        margin-right: 0;
        margin-bottom: 3px;
      }
    }

    &__title {
      line-height: 1.1;
    }
  }

  .terms {
    margin-left: auto;
    display: flex;
    align-items: center;
    font-weight: bold;
    color: #3F4956;

    @media (max-width: 959px) {
      font-size: 11px;
      flex-direction: column;
      text-align: center;
    }

    @media (max-width: 767px) {
      max-width: 67px;
    }

    &__icon {
      max-width: 20px;
      margin-right: 7px;

      @media (max-width: 959px) {
        margin-right: 0;
        margin-bottom: 3px;
      }
    }

    &__title {
      line-height: 1.1;
    }
  }
}
</style>
